<template>
	<v-card class="forum-card elevation-0 d-flex flex-column pt-7">
		<div class="forum-card__container">
			<div class="forum-card__outer">
				<div class="d-flex forum-card__list px-0">
					<router-link
						:to="{
							name: isCurrentUser(this.forum.user.user_id) ? 'profile' : 'user-by-id',
							params: isCurrentUser(this.forum.user.user_id)
								? []
								: { id: this.forum.user.user_id },
						}"
						class="d-flex justify-space-between text-decoration-none"
						style="width: 100%"
						:class="{ disabled: this.forum.user.user_hidden }"
					>
						<div
							class="d-flex justify-space-between align-center pa-4"
							style="width: 100%"
						>
							<div class="d-flex align-center">
								<Avatar class="mr-2" :size="50" :avatar="forum.user.user_logo" />
								<div
									class="d-flex flex-column justify-center gap-4 forum-card__meta"
								>
									{{ forum.user.visible_username }}
									<span class="forum-card__meta-bottom">{{
										forum.user.second_profile_row
									}}</span>
								</div>
							</div>
							<div class="forum-card__meta-time">{{ dateCreated }}</div>
						</div>
					</router-link>
					<v-divider />

					<v-card-title class="forum-card__title" v-html="formattedText" />

					<div v-if="forum.uploads && forum.uploads.length">
						<div
							class="d-flex flex-row flex-wrap w-full px-3 pb-4 mb-n2"
							v-for="attachment in this.forum.uploads"
							:key="attachment.upload_id"
						>
							<SessionAttachments style="display: flex" :attachment="attachment" />
						</div>
					</div>
				</div>

				<v-divider class="mt-3" />

				<div class="custom_container">
					<div class="d-flex">
						<div class="d-flex align-center mr-6">
							<Icon type="eye" color="var(--accent-color)" :size="22" :pr="8" />
							<div class="forum-card__counter-num">
								{{ forum.views_count }}
							</div>
						</div>
						<div class="d-flex align-center">
							<Icon type="comment" color="var(--accent-color)" :size="22" :pr="8" />
							<div class="forum-card__counter-num">
								{{ forum.comment_count }}
							</div>
						</div>
					</div>
					<div
						class="forum-card__follow"
						:class="isFollowed && 'forum-card__follow-active'"
						@click="onFollow"
					>
						{{ isFollowed ? 'Followed' : 'Follow' }}
					</div>
				</div>

				<div class="pa-8 post-card__comments-view">
					<div v-if="this.comments.length === 0" class="d-flex pa-4 text-center">
						<span style="width: 100%">There are no comments on this moments.</span>
					</div>
					<Comment
						v-else
						v-for="item in normalizedComments"
						:key="item.id"
						:comment="item"
						:post="{
							thread_id: item.threadId,
							user: item.user,
						}"
					/>
				</div>

				<v-divider />

				<div class="d-flex align-center px-4">
					<div style="width: 100%" v-if="this.isAuthenticated">
						<CommentForm
							class="mb-5 pl-0"
							:avatar="this.profile.user_logo"
							:parent="this.forum"
						/>
					</div>
				</div>
			</div>
		</div>
	</v-card>
</template>

<script>
import Vue from 'vue'
import VueScrollTo from 'vue-scrollto'
import Avatar from '@/components/Avatar'
import { forumFollow, forumUnfollow } from '@/api/forum'
import CommentForm from '@/components/Forms/CommentForm.vue'
import { mapGetters } from 'vuex'
import { normalizeComment } from '@/utils/helpers'
import Comment from '@/components/Comment.vue'
import SessionAttachments from '@/components/Resource/SessionAttachments.vue'

Vue.use(VueScrollTo)

export default {
	name: 'ForumCard',
	components: {
		CommentForm,
		Comment,
		Avatar,
		SessionAttachments,
	},

	data: () => ({
		isFollowed: false,
	}),
	props: {
		forum: {
			type: Object,
			default: () => {},
		},
	},
	computed: {
		...mapGetters('auth', ['profile', 'isAuthenticated']),
		...mapGetters('forum', ['comments']),
		formattedText() {
			const parser = new DOMParser()
			const doc = parser.parseFromString(this.forum.forum_text, 'text/html')
			return doc.body.innerHTML
		},
		dateCreated() {
			const date = new Date(this.forum.created * 1000)
			const options = { year: 'numeric', month: 'long', day: 'numeric' }
			return date.toLocaleDateString('en-US', options)
		},

		/**
		 * @returns Array
		 */
		normalizedComments: function () {
			return this.comments.map((item) => normalizeComment(item))
		},
	},
	methods: {
		onFollow() {
			if (this.isFollowed) {
				forumUnfollow(this.forum.forum_id)
			} else if (!this.isFollowed) {
				forumFollow({ forum_id: this.forum.forum_id })
			}

			this.isFollowed = !this.isFollowed
		},
		isCurrentUser(userId) {
			if (!this.profile) {
				return false
			}

			const { user_id: currentUserId } = this.profile
			return userId === currentUserId
		},
	},
	created() {
		if (this.forum) {
			this.isFollowed = this.forum.follow
		}
	},
}
</script>

<style lang="scss">
.forum-card {
	background: #f1f1f1 !important;

	&__container {
		width: 100%;
		height: 100%;
		border-radius: 10px !important;
		box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.06);
		background: white !important;
	}

	&__content-inner {
		color: rgb(74, 74, 74);
		font-size: 15px;
		font-family: Montserrat, sans-serif;
		font-weight: normal;
		line-height: 18px;
		padding-top: 10px;

		img {
			width: 100%;
		}

		p {
			margin-bottom: 0;
		}

		h1 {
			font-size: 30px;
			padding-bottom: 8px;
			line-height: 30px;
		}
	}

	&__counter {
		&-num {
			color: #697583;
			font-size: 15px;
			font-family: Montserrat, sans-serif;
			font-weight: bold;
			text-align: right;
		}
	}

	&__follow {
		color: #0055a3;
		font-size: 14px;
		font-family: Montserrat, sans-serif;
		font-weight: bold;
		text-decoration: underline;
		cursor: pointer;

		&-active {
			color: rgb(160, 160, 160);
		}
	}

	&__meta {
		font-family: Montserrat, sans-serif;
		font-style: normal;
		font-weight: bold;
		font-size: 18px;
		line-height: 20px;
		color: var(--username-category-title);
		letter-spacing: 0.36px;
		gap: 4px;

		&-bottom {
			color: var(--company-position-large);
			font-size: 16px;
			font-family: Montserrat, sans-serif;
			font-weight: normal;
			line-height: 16px;
		}

		&-time {
			color: rgb(155, 155, 155);
			font-size: 12px;
			font-family: Montserrat, sans-serif;
			font-weight: normal;
			text-align: right;
		}
	}

	&__outer {
	}

	&__list {
		position: relative;
		flex-direction: column;
	}

	&__title {
		color: #4a4a4a !important;
		font-size: 14px;
		font-family: Montserrat, sans-serif;
		font-weight: 400;
		letter-spacing: 0;
		line-height: 17.07px;
		font-style: normal;
		word-break: break-word;
		white-space: pre-wrap;
	}

	&__block-view {
		flex-direction: column;
		height: 73%;

		.post-preview-card__thumbnail-outer img {
			max-width: 100%;
		}
	}

	&__content {
		font-family: Montserrat, sans-serif;
		font-style: normal;
		font-weight: 500;
		font-size: 16px;
		line-height: 21px;
	}

	&__thumbnail {
		height: auto;
		width: 100%;
	}

	&__menu-dots {
		margin-left: 10px !important;
	}

	@media screen and (max-width: 1904px) {
		&__block {
			flex: 0 0 100%;
		}
		&__list {
			flex: 0 0 75%;
		}
	}
	@media screen and (max-width: 1264px) {
		&__block {
			flex: 0 0 100%;
		}
		&__list {
			flex: 0 0 66%;
		}
	}

	@media screen and (min-width: 1264px) {
		&__container {
			max-width: 600px;
		}
	}
	@media screen and (max-width: 768px) {
		&__thumbnail {
			height: 100%;
			max-width: 100%;
		}
		&__default-thumbnail {
			max-height: 250px;
			height: 100%;
		}
		&__outer {
			flex-direction: column;
		}
	}
}

.custom_container {
	display: flex;
	justify-content: space-between;
	padding: 15px 16px 6px;
}
</style>
